'use client';

import {
  Button,
  ButtonGroup,
  Dialog,
  Flex,
  Text,
  Toggle,
  Separator,
  Input,
} from '@/_components';
import { FC, useContext, useState } from 'react';
import { TaxReturnVoucherDialogProps } from './TaxReturnVoucherDialog.types';
import { AdminTaxReturnsContext } from '@/_contexts/admin/AdminTaxReturnsProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import { updateTaxReturn } from '@/_api/admin-tax-returns';
import { UserContext } from '@/_contexts/UserProvider';
import type { TaxReturn, Voucher } from '@/_types/models/taxReturns';
import styles from './TaxReturnVoucherDialog.module.css';
import { CompanyContext } from '@/_contexts/admin/CompanyProvider';

export const TaxReturnVoucherDialog: FC<TaxReturnVoucherDialogProps> = ({
  isOpen,
}) => {
  const [editingVoucherId, setEditingVoucherId] = useState<string | null>(null);
  const [editedVouchers, setEditedVouchers] = useState<Record<string, number>>(
    {},
  );
  const [pendingChanges, setPendingChanges] = useState<{
    amounts: Record<string, number>;
    paidStatus: Record<string, boolean>;
  }>({ amounts: {}, paidStatus: {} });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const { addToast } = useContext(ToastContext);
  const { token } = useContext(UserContext);
  const {
    selectedPaymentVouchersReturn,
    setSelectedPaymentVouchersReturn,
    getTaxReturns,
  } = useContext(AdminTaxReturnsContext);
  const { selectedCompany, refreshCompanyTaxReturns } =
    useContext(CompanyContext);
  const onClose = () => {
    setSelectedPaymentVouchersReturn(undefined);
    resetAll();
  };

  const getVoucherKey = (voucher: Voucher, index: number) => {
    return `${voucher.page}_${voucher.voucher_id}_${index}`;
  };

  const onToggleSelectedPaid = (voucher: Voucher, index: number) => {
    const voucherKey = getVoucherKey(voucher, index);
    const newPaidStatus = !voucher.is_paid;

    // Update pending changes
    setPendingChanges((prev) => ({
      ...prev,
      paidStatus: {
        ...prev.paidStatus,
        [voucherKey]: newPaidStatus,
      },
    }));

    // Update UI state
    setSelectedPaymentVouchersReturn((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        payment_vouchers: prev.payment_vouchers.map((v, index) =>
          getVoucherKey(v, index) === voucherKey
            ? { ...v, is_paid: newPaidStatus }
            : v,
        ),
      } as TaxReturn;
    });
  };

  const onEditVoucherAmount = (
    voucher: Voucher,
    amount: string,
    index: number,
  ) => {
    const voucherKey = getVoucherKey(voucher, index);
    const newAmount = parseFloat(amount) || 0;

    // Validate amount
    if (newAmount <= 0) {
      setErrors((prev) => ({
        ...prev,
        [voucherKey]: 'Amount must be greater than 0',
      }));
    } else {
      setErrors((prev) => {
        const { [voucherKey]: _, ...rest } = prev;
        return rest;
      });
    }

    setEditedVouchers((prev) => ({
      ...prev,
      [voucherKey]: newAmount,
    }));

    setPendingChanges((prev) => ({
      ...prev,
      amounts: {
        ...prev.amounts,
        [voucherKey]: newAmount,
      },
    }));
  };

  const startEditing = (voucher: Voucher, index: number) => {
    const voucherKey = getVoucherKey(voucher, index);
    setEditingVoucherId(voucherKey);

    // Set initial error if amount is 0 or null
    if (!voucher.amount || voucher.amount <= 0) {
      setErrors((prev) => ({
        ...prev,
        [voucherKey]: 'Amount must be greater than 0',
      }));
    }
  };

  const cancelEditing = () => {
    const currentKey = editingVoucherId;
    setEditingVoucherId(null);
    if (currentKey) {
      setErrors((prev) => {
        const { [currentKey]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const saveVoucherEdit = (voucher: Voucher, index: number) => {
    const voucherKey = getVoucherKey(voucher, index);
    if (!errors[voucherKey]) {
      setEditingVoucherId(null);
    }
  };

  const onSaveChanges = async () => {
    const hasErrors = Object.keys(errors).length > 0;
    const hasInvalidAmounts = selectedPaymentVouchersReturn?.payment_vouchers
      .filter((voucher) => !voucher.is_instruction_page)
      .some((voucher, index) => {
        const voucherKey = getVoucherKey(voucher, index);
        const amount =
          pendingChanges.amounts[voucherKey] !== undefined
            ? pendingChanges.amounts[voucherKey]
            : voucher.amount;
        return amount <= 0 || amount === null;
      });

    if (hasErrors) {
      addToast('error', 'Unknown error, please reach out to support');
      return;
    }

    if (hasInvalidAmounts) {
      addToast('error', 'Voucher amount must be greater than 0');
      return;
    }

    setIsSaving(true);
    try {
      const updatedVouchers = selectedPaymentVouchersReturn?.payment_vouchers
        .filter((voucher) => !voucher.is_instruction_page)
        .map((voucher, index) => {
          const voucherKey = getVoucherKey(voucher, index);
          return {
            ...voucher,
            amount:
              pendingChanges.amounts[voucherKey] !== undefined
                ? pendingChanges.amounts[voucherKey]
                : voucher.amount,
            is_paid:
              pendingChanges.paidStatus[voucherKey] !== undefined
                ? pendingChanges.paidStatus[voucherKey]
                : voucher.is_paid,
          };
        });

      await updateTaxReturn({
        id: selectedPaymentVouchersReturn?.id!,
        companyId: selectedPaymentVouchersReturn?.company_id!,
        token,
        payment_vouchers: updatedVouchers,
      } as any);

      setSelectedPaymentVouchersReturn({
        ...selectedPaymentVouchersReturn,
        payment_vouchers: updatedVouchers,
      } as TaxReturn);

      // Reset all pending changes
      setPendingChanges({ amounts: {}, paidStatus: {} });
      setEditedVouchers({});

      if (!!selectedCompany) {
        refreshCompanyTaxReturns();
      } else {
        getTaxReturns();
      }
      addToast('success', 'All changes saved successfully');
    } catch (error) {
      addToast('error', 'Failed to save changes', { error });
    }
    setIsSaving(false);
  };

  const getCurrentAmount = (voucher: Voucher, index: number): string => {
    const voucherKey = getVoucherKey(voucher, index);
    if (editedVouchers[voucherKey] !== undefined) {
      return editedVouchers[voucherKey].toString();
    }
    if (voucher.amount === null || voucher.amount === 0) {
      return '';
    }
    return voucher.amount.toString();
  };

  const resetAll = () => {
    setEditedVouchers({});
    setPendingChanges({ amounts: {}, paidStatus: {} });
    setErrors({});
    setEditingVoucherId(null);
  };

  return (
    <Dialog
      isOpen={isOpen}
      isWide={true}
      onClose={onClose}
      title="Update payment vouchers"
    >
      <Dialog.Content>
        <Flex isColumn gap={12} className={styles.voucherDialog}>
          <Flex isYCentered gap={12} className={styles.voucherHeader}>
            <Text className={styles.voucherItem_Name}>Voucher</Text>
            <Text className={styles.voucherItem_DueDate}>Due Date</Text>
            <Text className={styles.voucherItem_Amount}>Amount</Text>
            <Text className={styles.voucherItem_Status}>Status</Text>
          </Flex>

          <Separator isLight={true} />

          {selectedPaymentVouchersReturn?.payment_vouchers
            ?.filter((voucher) => !voucher.is_instruction_page)
            .map((voucher, index) => (
              <Flex key={voucher.id} isYCentered gap={12}>
                <Flex isYCentered className={styles.voucherItem_Name}>
                  <Text>
                    {voucher.custom_payment_name ||
                      'Voucher ' + voucher.voucher_id}{' '}
                    {voucher.quarter && `(${voucher.quarter})`}
                  </Text>
                  {voucher.payment_link && (
                    <Button
                      intent="secondary"
                      size="tiny"
                      withTooltip={true}
                      value="Go to Payment Link"
                      isIconOnly={true}
                      iconLeft="link"
                      onClick={() => {
                        window.open(voucher.payment_link, '_blank');
                      }}
                    />
                  )}
                </Flex>
                {voucher.due_date ? (
                  <Text className={styles.voucherItem_DueDate}>
                    {voucher.due_date}
                  </Text>
                ) : voucher.is_due_asap ? (
                  <Text className={styles.voucherItem_DueDate}>ASAP</Text>
                ) : (
                  <Text className={styles.voucherItem_DueDate}>unknown</Text>
                )}
                <Flex isYCentered gap={4} className={styles.voucherItem_Amount}>
                  {editingVoucherId === getVoucherKey(voucher, index) ? (
                    <>
                      <Input
                        value={getCurrentAmount(voucher, index)}
                        label="Amount"
                        placeholder="ex. 123"
                        isLabelHidden={true}
                        onChange={(e) =>
                          onEditVoucherAmount(voucher, e.target.value, index)
                        }
                        helperText={errors[getVoucherKey(voucher, index)]}
                        helperTextColor="critical"
                      />
                      <ButtonGroup isSameLine={true} isSmallGroup={true}>
                        <Button
                          intent="secondary"
                          size="tiny"
                          onClick={cancelEditing}
                          value="Cancel"
                          isIconOnly={true}
                          iconLeft="x"
                        />
                        <Button
                          intent="primary"
                          size="tiny"
                          onClick={() => saveVoucherEdit(voucher, index)}
                          value="Save"
                          isIconOnly={true}
                          iconLeft="check"
                        />
                      </ButtonGroup>
                    </>
                  ) : (
                    <>
                      <Text>${getCurrentAmount(voucher, index) || 0}</Text>
                      <Button
                        intent="secondary"
                        size="tiny"
                        onClick={() => startEditing(voucher, index)}
                        value="Edit"
                        isIconOnly={true}
                        iconLeft="pencil"
                        tooltip="Edit"
                        withTooltip={true}
                      />
                    </>
                  )}
                </Flex>
                <Flex gap={8} isYCentered className={styles.voucherItem_Status}>
                  <Toggle
                    isChecked={voucher.is_paid}
                    onChange={() => onToggleSelectedPaid(voucher, index)}
                    isLabelHidden={true}
                  />
                  <Text color={voucher.is_paid ? 'success' : 'caution'}>
                    {voucher.is_paid ? 'Paid' : 'Unpaid'}
                  </Text>
                </Flex>
              </Flex>
            ))}
        </Flex>
      </Dialog.Content>
      <Dialog.Footer>
        <ButtonGroup align="right">
          <Button
            intent="primary"
            isDisabled={isSaving}
            onClick={onSaveChanges}
            value="Save changes"
          />
        </ButtonGroup>
      </Dialog.Footer>
    </Dialog>
  );
};
